import { useToast } from '@app/core/notifications';
import { pushTo } from '@app/core/routing';
import {
  EnrichedCompositionDetailDto,
  EnrichedVideoEditorEditListItemDto,
} from '@app/modules/compose/composition.types';
import { composeRouteGenerator } from '@app/modules/compose/routing/compose-route-generator';
import { SocialPlatform, socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { useLastUsedPlatformForStation } from '@app/modules/social-export/hooks/useLastUsedPlatformForStation';
import {
  type SocialExportPublishForm,
  useSocialExportUploadConnectionFlow,
} from '@app/modules/social-export/hooks/useSocialExportUploadConnectionFlow';
import { publishMessages } from '@app/modules/social-export/publish';
import { getEditRenderedVideoUrl } from '@app/modules/video-editions';
import {
  SocialExportGetStationConnectionStateDto,
  useWebAppCreateSocialUploadCompositionsDetail,
  useWebAppGetCompositionsDetailById,
  useWebAppGetCompositionsDetailEdits,
  webAppGetCompositionsDetailSocialUploadsQueryKey,
  WebAppCreateSocialUploadDtoSourceTypeEnum,
} from '@mi-front/api-v2';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';

export type ComposeSocialExportPublishForm = SocialExportPublishForm;

export interface UseComposeSocialExportUploadPageResult {
  compositionQuery: ReturnType<typeof useWebAppGetCompositionsDetailById>;
  editsListQuery: ReturnType<typeof useWebAppGetCompositionsDetailEdits>;
  /** Resolved platform, or undefined while last-used is still loading / unknown id redirected. */
  platform: SocialPlatform | undefined;
  selectedSource: string;
  setSelectedSource: (value: string) => void;
  edits: EnrichedVideoEditorEditListItemDto[];
  hasAvailableSources: boolean;
  showMediaSelector: boolean;
  hasSelectedRenderedEdit: boolean;
  renderedVideoUrl: string | null;
  PublishForm: ComposeSocialExportPublishForm | undefined;
  handleUpload: (
    composition: EnrichedCompositionDetailDto,
    publishData: Record<string, unknown>,
  ) => Promise<void>;
  connections: SocialExportGetStationConnectionStateDto[];
  selectedConnectionId: string | null;
  setSelectedConnectionId: (id: string | null) => void;
  areConnectionsResolved: boolean;
  settingsQuery: ReturnType<typeof useSocialExportUploadConnectionFlow>['settingsQuery'];
  /** True while resolving last-used platform for a platform-less URL. */
  isResolvingPlatform: boolean;
}

/**
 * Owns routing, source selection, connection settings, rendered preview URL, and the
 * create-upload mutation for the composition social export upload flow.
 */
export function useComposeSocialExportUploadPage(): UseComposeSocialExportUploadPageResult {
  const { compositionId, platform: platformParam } = useParams<{
    compositionId: string;
    platform?: string;
  }>();
  const { $t } = useIntl();
  const { notifyInfo, notifyError } = useToast();
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const editIdFromUrl = searchParams.get('editId') ?? searchParams.get('projectId') ?? undefined;

  const compositionQuery = useWebAppGetCompositionsDetailById(compositionId!, {
    query: { enabled: !!compositionId },
  });

  const editsListQuery = useWebAppGetCompositionsDetailEdits(compositionId!, {
    query: { enabled: !!compositionId },
  });

  const edits = editsListQuery.data ?? [];
  const editsWithRender = edits.filter((edit) => edit.renderJob?.status === 'COMPLETED');
  const stationId = compositionQuery.data?.stationId ?? undefined;

  const [selectedSource, setSelectedSource] = useState<string>(() => editIdFromUrl ?? '');

  useEffect(() => {
    if (editIdFromUrl) {
      setSelectedSource(editIdFromUrl);
    }
  }, [editIdFromUrl]);

  useEffect(() => {
    if (editIdFromUrl || selectedSource) return;
    const firstId = editsWithRender[0]?.id;
    if (firstId) setSelectedSource(firstId);
  }, [editIdFromUrl, editsWithRender, selectedSource]);

  const selectedEdit =
    selectedSource !== '' ? (edits.find((edit) => edit.id === selectedSource) ?? null) : null;

  const { url: renderedVideoUrl } = getEditRenderedVideoUrl(selectedEdit, {
    mode: 'playback',
  });

  const showMediaSelector = editsWithRender.length > 0;
  const hasAvailableSources = editsWithRender.length > 0;
  const hasSelectedRenderedEdit = editsWithRender.some((edit) => edit.id === selectedSource);

  const { platform: lastUsedPlatform, areConnectionsResolved: areLastUsedResolved } =
    useLastUsedPlatformForStation(stationId);

  const platformFromUrl = useMemo(() => {
    if (!platformParam) {
      return undefined;
    }
    try {
      return socialPlatforms.get(platformParam.toUpperCase());
    } catch {
      return null;
    }
  }, [platformParam]);

  useEffect(() => {
    if (platformFromUrl === null && compositionQuery.data) {
      pushTo(composeRouteGenerator.publish(compositionQuery.data.id), true);
    }
  }, [platformFromUrl, compositionQuery.data]);

  const platform = platformFromUrl || lastUsedPlatform;

  useEffect(() => {
    if (platformParam || !platform || !compositionQuery.data) {
      return;
    }
    pushTo(
      composeRouteGenerator.upload(compositionQuery.data.id, platform.id, editIdFromUrl),
      true,
    );
  }, [platformParam, platform, compositionQuery.data, editIdFromUrl]);

  const connectionFlow = useSocialExportUploadConnectionFlow(
    stationId,
    platform ?? socialPlatforms.all()[0]!,
  );

  const uploadMutation = useWebAppCreateSocialUploadCompositionsDetail({
    mutation: {
      onSuccess: async () => {
        if (!compositionId) {
          return;
        }
        await queryClient.invalidateQueries({
          queryKey: webAppGetCompositionsDetailSocialUploadsQueryKey(compositionId),
        });
        notifyInfo($t(publishMessages.publicationStarted));
      },
      onError: () => {
        notifyError($t(publishMessages.startPublicationError));
      },
    },
  });

  const handleUpload = async (
    composition: EnrichedCompositionDetailDto,
    publishData: Record<string, unknown>,
  ) => {
    try {
      if (!platform) {
        return;
      }

      if (!hasSelectedRenderedEdit) {
        notifyError($t(publishMessages.noRenderedEdit));
        return;
      }

      if (!composition.stationId) {
        notifyError($t(publishMessages.noStation));
        return;
      }

      if (connectionFlow.isConnectionsLoading) {
        notifyError($t(publishMessages.connectionNotReady));
        return;
      }

      if (!connectionFlow.selectedConnectionId) {
        notifyError(
          $t(publishMessages.noOAuthConnectionForPlatform, {
            platformName: platform.name,
          }),
        );
        return;
      }

      await uploadMutation.mutateAsync({
        id: composition.id,
        data: {
          sourceType: WebAppCreateSocialUploadDtoSourceTypeEnum.VIDEO_EDITOR_EDIT,
          sourceId: selectedSource,
          connectionId: connectionFlow.selectedConnectionId,
          data: publishData,
        },
      });
      pushTo(composeRouteGenerator.publish(composition.id));
    } catch (error: unknown) {
      notifyError((error as Error).message);
    }
  };

  const isResolvingPlatform = platformFromUrl === undefined && (!areLastUsedResolved || !platform);

  return {
    compositionQuery,
    editsListQuery,
    platform: platformFromUrl === null ? undefined : platform,
    selectedSource,
    setSelectedSource,
    edits,
    hasAvailableSources,
    showMediaSelector,
    hasSelectedRenderedEdit,
    renderedVideoUrl,
    PublishForm: platform ? connectionFlow.PublishForm : undefined,
    handleUpload,
    connections: connectionFlow.connections,
    selectedConnectionId: connectionFlow.selectedConnectionId,
    setSelectedConnectionId: connectionFlow.setSelectedConnectionId,
    areConnectionsResolved: connectionFlow.areConnectionsResolved,
    settingsQuery: connectionFlow.settingsQuery,
    isResolvingPlatform,
  };
}
